import {
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Title,
  Tooltip
} from "@mantine/core";
import { Markup } from "interweave";
import {
  IconCheck,
  IconDownload,
  IconEdit,
  IconFile
} from "@tabler/icons-react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { downloadDocumentFile } from "@/api/documents/downloadFile";
import { getDocumentQuery } from "@/api";
import { DataDisplay } from "@/components/DataDisplay";
import { openBlobInNewTabFromUserGesture } from "@/utils/openBlobInNewTabFromUserGesture";
import {
  canAddOrEditProspectDocuments,
  canViewProspectDocuments
} from "@/security/logic";

export const Route = createFileRoute(
  "/prospects/$prospectId/documents/$documentId/"
)({
  component: DocumentViewComponent,
  beforeLoad: ({ context, location }) => {
    const { userClaim, prospect } = context;

    // redirect from $documentId index if user lacks view permissions
    if (
      prospect &&
      !canViewProspectDocuments(userClaim, prospect.communityId)
    ) {
      throw redirect({
        to: "/notAuthorized/$",
        params: { _splat: location.href }
      });
    }
  }
});

function DocumentViewComponent() {
  const { prospectId, documentId } = Route.useParams();
  const navigate = useNavigate();
  const { prospect, userClaim, apiHook } = Route.useRouteContext();
  const { secureAxios } = apiHook;

  const { data: document, isPending } = useQuery(
    getDocumentQuery(secureAxios, documentId)
  );

  const hasEditPermission = canAddOrEditProspectDocuments(
    userClaim,
    prospect?.communityId ?? 0
  );

  const handleClose = useCallback(() => {
    void navigate({
      to: "/prospects/$prospectId/documents",
      params: { prospectId }
    });
  }, [navigate, prospectId]);

  const handleEdit = useCallback(() => {
    void navigate({
      to: "/prospects/$prospectId/documents/$documentId/edit",
      params: { prospectId, documentId }
    });
  }, [navigate, prospectId, documentId]);

  const [downloading, setDownloading] = useState(false);
  const handleDownload = useCallback(async () => {
    if (!document?.id || !document.fileName) {
      return;
    }
    setDownloading(true);
    try {
      await openBlobInNewTabFromUserGesture(
        () => downloadDocumentFile(secureAxios, document.id.toString()),
        { fileName: document.fileName }
      );
    } finally {
      setDownloading(false);
    }
  }, [document?.id, document?.fileName, secureAxios]);

  if (isPending) {
    return (
      <Modal
        opened={true}
        closeOnClickOutside={false}
        closeOnEscape={false}
        onClose={handleClose}
        title="Loading..."
        centered
      >
        <Text>Loading document...</Text>
      </Modal>
    );
  }

  if (!document) {
    return (
      <Modal
        opened={true}
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
        onClose={handleClose}
        title="Document Not Found"
        centered
      >
        <Text>The requested document could not be found.</Text>
        <Group justify="end" mt="md">
          <Button onClick={handleClose}>Close</Button>
        </Group>
      </Modal>
    );
  }

  return (
    <Modal
      opened={true}
      closeOnClickOutside={false}
      closeOnEscape={false}
      onClose={handleClose}
      title="View Document or Note"
      size="xl"
      centered
      styles={{ body: { overflow: "hidden" } }}
    >
      <Stack gap="md">
        <Group gap="xl">
          <DataDisplay label="Title" value={document.title} />
          <DataDisplay label="Type" value={document.type.displayName} />
        </Group>

        <Box>
          <Group align="center" mb={4}>
            <Title order={5} mt={0} mb={0}>
              Notes
            </Title>
            {document.includeDetailsInLivingStory && (
              <Text size="sm" inline fw={500}>
                <Group align="center" gap={8}>
                  <IconCheck size={24} /> Included in Living Story
                </Group>
              </Text>
            )}
          </Group>

          <Markup className="markup-content" content={document.detailsHtml} />
        </Box>

        {document.fileName && (
          <Stack gap={4}>
            <Title order={5}>File</Title>
            <Box
              py="md"
              px="md"
              style={{
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: "var(--mantine-radius-sm)"
              }}
            >
              <Group justify="space-between">
                <Group>
                  <IconFile
                    size={24}
                    color="var(--mantine-color-optimaBlue-9)"
                    stroke={1.5}
                  />
                  <Text size="sm" fw={500}>
                    {document.fileName}
                  </Text>
                </Group>
                <Button
                  variant="subtle"
                  size="sm"
                  leftSection={
                    downloading ? (
                      <Loader size={16} color="blue" />
                    ) : (
                      <IconDownload size={16} />
                    )
                  }
                  onClick={() => void handleDownload()}
                  disabled={downloading}
                  loading={downloading}
                >
                  Download
                </Button>
              </Group>
            </Box>
          </Stack>
        )}

        <Group justify="end" mt="md">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
          <Tooltip
            disabled={hasEditPermission}
            label={
              hasEditPermission
                ? undefined
                : "You do not have access to edit documents or notes."
            }
          >
            <Button
              leftSection={<IconEdit size={16} />}
              onClick={handleEdit}
              disabled={!hasEditPermission}
            >
              Edit
            </Button>
          </Tooltip>
        </Group>
      </Stack>
    </Modal>
  );
}
